import type { ForegroundNotification, NotificationPermissionState, NotificationRoute, NotificationTap } from "./types"

const NOTIFICATION_HANDOFF_ORIGIN = "https://wildlark.ai"
const NOTIFICATION_HANDOFF_PATH = "/task"

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function requiredString(value: unknown, field: string): string {
    if (typeof value !== "string" || value.trim().length === 0) throw new Error(`Notification route is missing ${field}`)
    return value
}

export function normalizeNotificationPermission(value: string): NotificationPermissionState {
    if (value === "granted") return "granted"
    if (value === "denied") return "denied"
    if (value === "prompt" || value === "prompt-with-rationale") return "prompt"
    throw new Error("Native notification permission returned an unknown state")
}

export function parseNotificationRoute(value: unknown): NotificationRoute {
    if (!isRecord(value)) throw new Error("Notification route must be an object")
    const allowedFields = new Set(["version", "nodeId", "workspaceId", "taskId"])
    if (Object.keys(value).some((field) => !allowedFields.has(field))) throw new Error("Notification route contains unsupported fields")
    const rawVersion = value.version
    const version = typeof rawVersion === "string" ? Number(rawVersion) : rawVersion
    if (version !== 1) throw new Error("Notification route has an unsupported version")

    return {
        version: 1,
        nodeId: requiredString(value.nodeId, "nodeId"),
        workspaceId: requiredString(value.workspaceId, "workspaceId"),
        taskId: requiredString(value.taskId, "taskId"),
    }
}

export function parseNotificationTap(value: unknown): NotificationTap {
    if (!isRecord(value)) throw new Error("Notification tap must be an object")
    return {
        actionId: typeof value.actionId === "string" ? value.actionId : "tap",
        route: parseNotificationRoute(value.data),
    }
}

export function parseForegroundNotification(value: unknown): ForegroundNotification {
    if (!isRecord(value) || !isRecord(value.data)) throw new Error("Foreground notification data must be an object")
    const phase = value.data.phase
    if (phase !== "needs-input" && phase !== "succeeded" && phase !== "failed") throw new Error("Foreground notification phase is invalid")
    const showAlert = value.data.showAlert
    if (showAlert !== true && showAlert !== "true") throw new Error("Foreground notification is not an alert")
    return {
        phase,
        route: parseNotificationRoute({
            version: value.data.version,
            nodeId: value.data.nodeId,
            workspaceId: value.data.workspaceId,
            taskId: value.data.taskId,
        }),
    }
}

export function readNotificationRouteUrl(value: string): NotificationRoute | null {
    try {
        const url = new URL(value)
        if (url.origin !== NOTIFICATION_HANDOFF_ORIGIN || url.pathname !== NOTIFICATION_HANDOFF_PATH || url.search) return null
        const fragment = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash
        const params = new URLSearchParams(fragment)
        const allowed = new Set(["v", "nodeId", "workspaceId", "taskId"])
        if ([...params.keys()].some((key) => !allowed.has(key))) return null
        if ([...allowed].some((key) => params.getAll(key).length !== 1)) return null
        return parseNotificationRoute({
            version: params.get("v"),
            nodeId: params.get("nodeId"),
            workspaceId: params.get("workspaceId"),
            taskId: params.get("taskId"),
        })
    } catch {
        return null
    }
}
